/** Shared pieces of the plans page, the upgrade dialog and the Subscription tab. */

import type { KeyboardEvent, ReactNode } from 'react'

import { Button } from '@/components/ui/Button'
import type { Tone } from '@/components/ui/StatusPill'
import { cn, formatDate } from '@/lib/utils'
import type {
  BillingPeriod,
  PlanFeatures,
  PlanKey,
  PlanResponse,
  PlansResponse,
  SubscriptionResponse,
  SubscriptionSummary,
} from '@/types/api'

import { FIRST_PERIOD, discountedCents, type AppliedCoupon } from './coupon'

/** The plan everyone is steered toward (shown as recommended). */
export const RECOMMENDED_PLAN: PlanKey = 'power'

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------
const USD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })
const USD_WHOLE = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
})

/** Cents → "$19" (or "$15.17" when there are cents). */
export function formatCents(cents: number): string {
  return cents % 100 === 0 ? USD_WHOLE.format(cents / 100) : USD.format(cents / 100)
}

export const formatCount = (value: number) => new Intl.NumberFormat('en-US').format(value)

export const PERIOD_SUFFIX: Record<BillingPeriod, string> = {
  monthly: '/mo',
  quarterly: '/quarter',
  annual: '/year',
}

export const PERIOD_NOUN: Record<BillingPeriod, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annual: 'Annual',
}

/** How much a period saves per month against paying monthly, in whole percent (0 when none). */
export function planSavings(plan: PlanResponse, period: BillingPeriod): number {
  const monthly = plan.prices.monthly?.perMonthCents
  const perMonth = plan.prices[period]?.perMonthCents
  if (!monthly || !perMonth || perMonth >= monthly) return 0
  return Math.round((1 - perMonth / monthly) * 100)
}

/** The saving a period gives on every plan (the smallest, so the toggle never overpromises). */
export function periodSavings(plans: PlanResponse[], period: BillingPeriod): number {
  if (period === 'monthly' || plans.length === 0) return 0
  return Math.min(...plans.map((plan) => planSavings(plan, period)))
}

/** The status pill of a subscription. */
export function subscriptionStatus(sub: SubscriptionSummary): { label: string; tone: Tone } {
  const ends = formatDate(sub.currentPeriodEnd)
  switch (sub.status) {
    case 'active':
      return sub.cancelAtPeriodEnd
        ? { label: `Cancelled — ends ${ends}`, tone: 'warning' }
        : { label: 'Active', tone: 'success' }
    case 'past_due':
      return { label: 'Past due', tone: 'danger' }
    case 'canceled':
      return new Date(sub.currentPeriodEnd).getTime() > Date.now()
        ? { label: `Cancelled — ends ${ends}`, tone: 'warning' }
        : { label: 'Cancelled', tone: 'neutral' }
    case 'expired':
      return { label: 'Expired', tone: 'neutral' }
    case 'incomplete':
      return { label: 'Payment pending', tone: 'warning' }
    default:
      return { label: String(sub.status), tone: 'neutral' }
  }
}

/** The plan the user is on and paying for, when it is in force. */
export function activePlanKey(data: SubscriptionResponse | null | undefined): PlanKey | null {
  return data?.active && data.subscription ? data.subscription.plan : null
}

// ---------------------------------------------------------------------------
// Feature rows
// ---------------------------------------------------------------------------
export interface FeatureRow {
  key: string
  label: string
  on: boolean
}

/** What a plan switches on, as checklist rows (the same rows on every screen). */
export function featureRows(features: PlanFeatures): FeatureRow[] {
  return [
    { key: 'resumeTailoring', label: 'Resume tailored to each job', on: features.resumeTailoring },
    features.coverLetterTailoring
      ? { key: 'coverLetter', label: 'Cover letter written for each job', on: true }
      : { key: 'coverLetter', label: 'One standard cover letter', on: features.fixedCoverLetter },
    { key: 'autoApply', label: 'Auto Apply — no Apply button to press', on: features.autoApply },
    { key: 'scheduling', label: 'Schedule the days and hours', on: features.scheduling },
    { key: 'dailyReport', label: 'Daily email report', on: features.dailyReport },
  ]
}

export function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="m3.5 8.5 3 3 6-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

export function CrossIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="m5 5 6 6M11 5l-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

export function FeatureList({ rows, className }: { rows: FeatureRow[]; className?: string }) {
  return (
    <ul className={cn('space-y-2', className)}>
      {rows.map((row) => (
        <li key={row.key} className="flex items-start gap-2.5 text-[13.5px] leading-snug">
          {row.on ? (
            <CheckIcon className="mt-px h-4 w-4 shrink-0 text-success" />
          ) : (
            <CrossIcon className="mt-px h-4 w-4 shrink-0 text-ink-faint" />
          )}
          <span className={row.on ? 'text-ink-soft' : 'text-ink-faint line-through decoration-ink-faint/40'}>
            {row.label}
            <span className="sr-only">{row.on ? ' (included)' : ' (not included)'}</span>
          </span>
        </li>
      ))}
    </ul>
  )
}

// ---------------------------------------------------------------------------
// Period toggle
// ---------------------------------------------------------------------------
export function PeriodToggle({
  data,
  value,
  onChange,
  size = 'md',
  className,
}: {
  data: PlansResponse
  value: BillingPeriod
  onChange: (period: BillingPeriod) => void
  size?: 'sm' | 'md'
  className?: string
}) {
  const periods = data.periods
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return
    event.preventDefault()
    const next = periods[(index + (event.key === 'ArrowRight' ? 1 : -1) + periods.length) % periods.length]
    if (next) onChange(next.key)
  }
  return (
    <div
      role="radiogroup"
      aria-label="Billing period"
      className={cn(
        'grid w-full auto-cols-fr grid-flow-col rounded-[11px] border border-line bg-surface-muted p-0.5 sm:inline-grid sm:w-auto',
        className,
      )}
    >
      {periods.map((period, index) => {
        const selected = period.key === value
        const save = periodSavings(data.plans, period.key)
        return (
          <button
            key={period.key}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(period.key)}
            onKeyDown={(event) => onKeyDown(event, index)}
            className={cn(
              'flex min-w-0 flex-col items-center justify-center rounded-[9px] px-3 font-medium transition-all sm:px-4',
              size === 'sm' ? 'min-h-[40px] py-1 text-[12.5px]' : 'min-h-[48px] py-1.5 text-[13.5px]',
              selected ? 'bg-surface text-ink shadow-card' : 'text-ink-muted hover:text-ink-soft',
            )}
          >
            <span className="truncate">{period.label}</span>
            <span
              className={cn(
                'text-[11px] leading-tight',
                save > 0 ? 'text-success' : 'text-ink-faint',
              )}
            >
              {save > 0 ? `Save ${save}%` : 'Flexible'}
            </span>
          </button>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// The choose / switch button
// ---------------------------------------------------------------------------
export interface PlanAction {
  label: string
  disabled: boolean
}

/** What a plan's button says and whether it can be pressed. */
export function planAction({
  plan,
  period,
  subscription,
  billingEnabled,
}: {
  plan: PlanResponse
  period: BillingPeriod
  subscription: SubscriptionResponse | null | undefined
  billingEnabled: boolean
}): PlanAction {
  const current = activePlanKey(subscription)
  const sub = subscription?.subscription
  if (current === plan.key && sub?.period === period) return { label: 'Current plan', disabled: true }
  if (current === plan.key) return { label: `Switch to ${PERIOD_NOUN[period].toLowerCase()}`, disabled: !billingEnabled }
  return {
    label: current ? `Switch to ${plan.name}` : `Choose ${plan.name}`,
    disabled: !billingEnabled,
  }
}

// ---------------------------------------------------------------------------
// Plan card
// ---------------------------------------------------------------------------
export function PlanCard({
  plan,
  period,
  current,
  recommended,
  action,
  pending,
  onChoose,
  footnote,
  coupon,
}: {
  plan: PlanResponse
  period: BillingPeriod
  current: boolean
  recommended: boolean
  action: PlanAction
  pending: boolean
  onChoose: () => void
  footnote?: ReactNode
  /** A valid coupon: the price shows before → after. */
  coupon?: AppliedCoupon | null
}) {
  const price = plan.prices[period]
  const save = planSavings(plan, period)
  const off = price && coupon && coupon.discountPct > 0 && !current ? discountedCents(price.amountCents, coupon.discountPct) : null
  return (
    <article
      className={cn(
        'card relative flex min-w-0 flex-col p-5',
        recommended && 'border-brand/50 ring-2 ring-brand/15',
        current && !recommended && 'border-ink/30',
      )}
      aria-label={`${plan.name} plan`}
    >
      <header className="flex flex-wrap items-center gap-2">
        <h2 className="text-[17px] font-semibold text-ink">{plan.name}</h2>
        {recommended && (
          <span className="rounded-pill bg-brand px-2 py-0.5 text-[11px] font-semibold text-oncolor">
            Recommended
          </span>
        )}
        {current && (
          <span className="rounded-pill bg-ink/[0.07] px-2 py-0.5 text-[11px] font-semibold text-ink-soft">
            Current plan
          </span>
        )}
      </header>
      <p className="mt-1 text-[13px] leading-relaxed text-ink-muted">{plan.tagline}</p>

      <div className="mt-4">
        {off !== null && price && coupon ? (
          <p className="flex flex-wrap items-baseline gap-x-1.5" aria-label={`${formatCents(price.amountCents)}, ${formatCents(off)} with ${coupon.code}`}>
            <span className="num text-[17px] font-medium leading-none text-ink-faint line-through decoration-ink-faint/70">{formatCents(price.amountCents)}</span>
            <span aria-hidden="true" className="text-[14px] text-ink-faint">→</span>
            <span className="num text-[32px] font-semibold leading-none tracking-tight text-ink">{formatCents(off)}</span>
            <span className="text-[13px] text-ink-muted">{coupon.duration === 'once' ? FIRST_PERIOD[period] : PERIOD_SUFFIX[period]}</span>
          </p>
        ) : (
          <p className="flex flex-wrap items-baseline gap-x-1">
            <span className="num text-[32px] font-semibold leading-none tracking-tight text-ink">
              {price ? formatCents(price.amountCents) : '—'}
            </span>
            <span className="text-[13px] text-ink-muted">{PERIOD_SUFFIX[period]}</span>
          </p>
        )}
        <p className="mt-1.5 min-h-[18px] text-[12.5px] text-ink-muted">
          {off !== null && price && coupon ? (
            <span className="font-medium text-success">
              {coupon.duration === 'once' ? `${coupon.discountPct}% off, then ${formatCents(price.amountCents)}${PERIOD_SUFFIX[period]}` : `${coupon.discountPct}% off every payment`}
            </span>
          ) : period !== 'monthly' && price ? (
            <>
              <span className="num">{formatCents(price.perMonthCents)}</span> a month
              {save > 0 && <span className="ml-1.5 font-medium text-success">save {save}%</span>}
            </>
          ) : (
            'Billed monthly'
          )}
        </p>
      </div>

      <p className="mt-4 rounded-field bg-surface-muted px-3 py-2.5 text-[13px] text-ink-soft">
        <span className="num text-[18px] font-semibold text-ink">{formatCount(plan.monthlyApplications)}</span>{' '}
        applications a month
      </p>

      <ul className="mt-4 flex-1 space-y-2">
        {plan.highlights.map((line) => (
          <li key={line} className="flex items-start gap-2.5 text-[13.5px] leading-snug text-ink-soft">
            <CheckIcon className="mt-px h-4 w-4 shrink-0 text-brand" />
            {line}
          </li>
        ))}
      </ul>

      <Button
        className="mt-5 w-full"
        variant={recommended && !action.disabled ? 'primary' : 'secondary'}
        disabled={action.disabled}
        loading={pending}
        onClick={onChoose}
      >
        {action.label}
      </Button>
      {footnote && <p className="mt-2 text-center text-[12px] text-ink-faint">{footnote}</p>}
    </article>
  )
}

/** One plan as a row, for the upgrade dialog and the empty Subscription tab. */
export function PlanRow({
  plan,
  period,
  current,
  recommended,
  action,
  pending,
  onChoose,
  coupon,
}: {
  plan: PlanResponse
  period: BillingPeriod
  current: boolean
  recommended: boolean
  action: PlanAction
  pending: boolean
  onChoose: () => void
  coupon?: AppliedCoupon | null
}) {
  const price = plan.prices[period]
  const off = price && coupon && coupon.discountPct > 0 && !current ? discountedCents(price.amountCents, coupon.discountPct) : null
  return (
    <li
      className={cn(
        'flex flex-col gap-3 px-4 py-3.5 xs:flex-row xs:items-center',
        recommended && 'bg-brand-soft/50',
      )}
    >
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center gap-2 text-[15px] font-semibold text-ink">
          {plan.name}
          {recommended && (
            <span className="rounded-pill bg-brand px-2 py-0.5 text-[10.5px] font-semibold text-oncolor">
              Recommended
            </span>
          )}
          {current && (
            <span className="rounded-pill bg-ink/[0.07] px-2 py-0.5 text-[10.5px] font-semibold text-ink-soft">
              Current plan
            </span>
          )}
        </p>
        <p className="mt-0.5 text-[12.5px] text-ink-muted">
          <span className="num">{formatCount(plan.monthlyApplications)}</span> applications a month
          {plan.features.resumeTailoring ? ' · tailored' : ' · one resume'}
          {plan.features.autoApply ? ' · Auto Apply' : ''}
        </p>
      </div>
      <div className="flex items-center justify-between gap-3 xs:justify-end">
        {off !== null && price && coupon ? (
          <p className="text-right leading-tight">
            <span className="num mr-1 text-[12.5px] text-ink-faint line-through">{formatCents(price.amountCents)}</span>
            <span className="num text-[16px] font-semibold text-ink">{formatCents(off)}</span>
            <span className="block text-[11.5px] text-ink-muted">{coupon.duration === 'once' ? FIRST_PERIOD[period] : `${PERIOD_SUFFIX[period]} with ${coupon.code}`}</span>
          </p>
        ) : (
          <p className="text-right">
            <span className="num text-[16px] font-semibold text-ink">{price ? formatCents(price.amountCents) : '—'}</span>
            <span className="text-[12px] text-ink-muted">{PERIOD_SUFFIX[period]}</span>
          </p>
        )}
        <Button
          size="sm"
          variant={recommended && !action.disabled ? 'primary' : 'secondary'}
          disabled={action.disabled}
          loading={pending}
          onClick={onChoose}
          className="min-w-[96px]"
        >
          {action.disabled && action.label === 'Current plan' ? 'Current' : 'Choose'}
        </Button>
      </div>
    </li>
  )
}
